// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export type NotificationContactStatus =
  | "pending"
  | "active"
  | "declined"
  | "unsubscribed"
  | "disabled";
export type NotificationEventFamily = "internet_interruption" | "probe_unavailable";

export interface NotificationContact {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly salutation: string;
  readonly status: NotificationContactStatus;
  readonly verificationSentAt: string | null;
  readonly verifiedAt: string | null;
  readonly declinedAt: string | null;
  readonly unsubscribedAt: string | null;
  readonly disabledAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateNotificationContactInput {
  readonly email: string;
  readonly displayName: string;
  readonly salutation?: string;
}

export interface UpdateNotificationContactInput {
  readonly displayName: string;
  readonly salutation: string;
  readonly status: "pending" | "active" | "disabled";
}

export interface NotificationContactOptInResult {
  readonly status: "accepted";
  readonly contactId: string;
  readonly tenantId: string;
  readonly email: string;
}

export interface NotificationGroup {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly enabled: boolean;
  readonly contactIds: readonly string[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SaveNotificationGroupInput {
  readonly name: string;
  readonly description?: string;
  readonly enabled?: boolean;
  readonly contactIds?: readonly string[];
}

export interface NotificationTemplate {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly enabled: boolean;
  readonly subjectTemplate: string;
  readonly textTemplate: string;
  readonly htmlTemplate: string | null;
  readonly revision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SaveNotificationTemplateInput {
  readonly name: string;
  readonly description?: string;
  readonly enabled?: boolean;
  readonly subjectTemplate: string;
  readonly textTemplate: string;
  readonly htmlTemplate?: string | null;
}

export interface NotificationEventPolicy {
  readonly eventFamily: NotificationEventFamily;
  readonly enabled: boolean;
  readonly thresholdOverrideEnabled: boolean;
  readonly thresholdOverrideMs: number;
  readonly sendOpened: boolean;
  readonly sendRecovery: boolean;
  readonly openedTemplateId: string | null;
  readonly resolvedTemplateId: string | null;
  readonly groupIds: readonly string[];
  readonly systemThresholdMs: number;
  readonly systemSendOpened: boolean;
  readonly systemSendRecovery: boolean;
  readonly systemOpenedTemplateKey: string;
  readonly systemResolvedTemplateKey: string;
  readonly effectiveEnabled: boolean;
  readonly effectiveThresholdMs: number;
  readonly effectiveSendOpened: boolean;
  readonly effectiveSendRecovery: boolean;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

export interface UpdateNotificationEventPolicyInput {
  readonly enabled: boolean;
  readonly thresholdOverrideEnabled: boolean;
  readonly thresholdOverrideMs: number;
  readonly sendOpened: boolean;
  readonly sendRecovery: boolean;
  readonly openedTemplateId: string | null;
  readonly resolvedTemplateId: string | null;
  readonly groupIds: readonly string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Notification response field ${fieldName} is invalid`);
  }
  return value;
}

function readNullableString(value: unknown, fieldName: string): string | null {
  if (value === null) return null;
  return readString(value, fieldName);
}

function readBoolean(value: unknown, fieldName: string): boolean {
  if (typeof value !== "boolean") {
    throw new Error(`Notification response field ${fieldName} is invalid`);
  }
  return value;
}

function readInteger(value: unknown, fieldName: string): number {
  if (!Number.isInteger(value)) {
    throw new Error(`Notification response field ${fieldName} is invalid`);
  }
  return value as number;
}

function readStringArray(value: unknown, fieldName: string): readonly string[] {
  if (!Array.isArray(value) || !value.every((item: unknown): boolean => typeof item === "string")) {
    throw new Error(`Notification response field ${fieldName} is invalid`);
  }
  return value as string[];
}

function readContact(payload: unknown, fieldName: string): NotificationContact {
  if (!isRecord(payload)) throw new Error(`Notification response field ${fieldName} is invalid`);
  const status: string = readString(payload.status, `${fieldName}.status`);
  if (!["pending", "active", "declined", "unsubscribed", "disabled"].includes(status)) {
    throw new Error(`Notification response field ${fieldName}.status is invalid`);
  }
  return {
    id: readString(payload.id, `${fieldName}.id`),
    email: readString(payload.email, `${fieldName}.email`),
    displayName: readString(payload.displayName, `${fieldName}.displayName`),
    salutation: readString(payload.salutation, `${fieldName}.salutation`),
    status: status as NotificationContactStatus,
    verificationSentAt: readNullableString(payload.verificationSentAt, `${fieldName}.verificationSentAt`),
    verifiedAt: readNullableString(payload.verifiedAt, `${fieldName}.verifiedAt`),
    declinedAt: readNullableString(payload.declinedAt, `${fieldName}.declinedAt`),
    unsubscribedAt: readNullableString(payload.unsubscribedAt, `${fieldName}.unsubscribedAt`),
    disabledAt: readNullableString(payload.disabledAt, `${fieldName}.disabledAt`),
    createdAt: readString(payload.createdAt, `${fieldName}.createdAt`),
    updatedAt: readString(payload.updatedAt, `${fieldName}.updatedAt`),
  };
}

function readContactOptInResult(payload: unknown): NotificationContactOptInResult {
  if (!isRecord(payload) || payload.status !== "accepted") {
    throw new Error("Notification contact opt-in response is invalid");
  }
  return {
    status: payload.status,
    contactId: readString(payload.contactId, "contactId"),
    tenantId: readString(payload.tenantId, "tenantId"),
    email: readString(payload.email, "email"),
  };
}

function readGroup(payload: unknown, fieldName: string): NotificationGroup {
  if (!isRecord(payload)) throw new Error(`Notification response field ${fieldName} is invalid`);
  return {
    id: readString(payload.id, `${fieldName}.id`),
    name: readString(payload.name, `${fieldName}.name`),
    description: readString(payload.description, `${fieldName}.description`),
    enabled: readBoolean(payload.enabled, `${fieldName}.enabled`),
    contactIds: readStringArray(payload.contactIds, `${fieldName}.contactIds`),
    createdAt: readString(payload.createdAt, `${fieldName}.createdAt`),
    updatedAt: readString(payload.updatedAt, `${fieldName}.updatedAt`),
  };
}

function readTemplate(payload: unknown, fieldName: string): NotificationTemplate {
  if (!isRecord(payload)) throw new Error(`Notification response field ${fieldName} is invalid`);
  const revision: unknown = payload.revision;
  if (!Number.isInteger(revision)) {
    throw new Error(`Notification response field ${fieldName}.revision is invalid`);
  }
  return {
    id: readString(payload.id, `${fieldName}.id`),
    name: readString(payload.name, `${fieldName}.name`),
    description: readString(payload.description, `${fieldName}.description`),
    enabled: readBoolean(payload.enabled, `${fieldName}.enabled`),
    subjectTemplate: readString(payload.subjectTemplate, `${fieldName}.subjectTemplate`),
    textTemplate: readString(payload.textTemplate, `${fieldName}.textTemplate`),
    htmlTemplate: readNullableString(payload.htmlTemplate, `${fieldName}.htmlTemplate`),
    revision: revision as number,
    createdAt: readString(payload.createdAt, `${fieldName}.createdAt`),
    updatedAt: readString(payload.updatedAt, `${fieldName}.updatedAt`),
  };
}

function readPolicy(payload: unknown, fieldName: string): NotificationEventPolicy {
  if (!isRecord(payload)) throw new Error(`Notification response field ${fieldName} is invalid`);
  const eventFamily: string = readString(payload.eventFamily, `${fieldName}.eventFamily`);
  if (eventFamily !== "internet_interruption" && eventFamily !== "probe_unavailable") {
    throw new Error(`Notification response field ${fieldName}.eventFamily is invalid`);
  }
  return {
    eventFamily,
    enabled: readBoolean(payload.enabled, `${fieldName}.enabled`),
    thresholdOverrideEnabled: readBoolean(payload.thresholdOverrideEnabled, `${fieldName}.thresholdOverrideEnabled`),
    thresholdOverrideMs: readInteger(payload.thresholdOverrideMs, `${fieldName}.thresholdOverrideMs`),
    sendOpened: readBoolean(payload.sendOpened, `${fieldName}.sendOpened`),
    sendRecovery: readBoolean(payload.sendRecovery, `${fieldName}.sendRecovery`),
    openedTemplateId: readNullableString(payload.openedTemplateId, `${fieldName}.openedTemplateId`),
    resolvedTemplateId: readNullableString(payload.resolvedTemplateId, `${fieldName}.resolvedTemplateId`),
    groupIds: readStringArray(payload.groupIds, `${fieldName}.groupIds`),
    systemThresholdMs: readInteger(payload.systemThresholdMs, `${fieldName}.systemThresholdMs`),
    systemSendOpened: readBoolean(payload.systemSendOpened, `${fieldName}.systemSendOpened`),
    systemSendRecovery: readBoolean(payload.systemSendRecovery, `${fieldName}.systemSendRecovery`),
    systemOpenedTemplateKey: readString(payload.systemOpenedTemplateKey, `${fieldName}.systemOpenedTemplateKey`),
    systemResolvedTemplateKey: readString(payload.systemResolvedTemplateKey, `${fieldName}.systemResolvedTemplateKey`),
    effectiveEnabled: readBoolean(payload.effectiveEnabled, `${fieldName}.effectiveEnabled`),
    effectiveThresholdMs: readInteger(payload.effectiveThresholdMs, `${fieldName}.effectiveThresholdMs`),
    effectiveSendOpened: readBoolean(payload.effectiveSendOpened, `${fieldName}.effectiveSendOpened`),
    effectiveSendRecovery: readBoolean(payload.effectiveSendRecovery, `${fieldName}.effectiveSendRecovery`),
    createdAt: readNullableString(payload.createdAt, `${fieldName}.createdAt`),
    updatedAt: readNullableString(payload.updatedAt, `${fieldName}.updatedAt`),
  };
}

export async function listNotificationContacts(tenantId: string): Promise<readonly NotificationContact[]> {
  const payload: unknown = await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-contacts`);
  if (!isRecord(payload) || !Array.isArray(payload.contacts)) {
    throw new Error("Notification contact list response is invalid");
  }
  return payload.contacts.map((contact: unknown, index: number): NotificationContact =>
    readContact(contact, `contacts.${index}`),
  );
}

export async function createNotificationContact(
  tenantId: string,
  input: CreateNotificationContactInput,
): Promise<NotificationContact> {
  return readContact(
    await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-contacts`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
    "contact",
  );
}

export async function confirmNotificationContactOptIn(
  token: string,
): Promise<NotificationContactOptInResult> {
  return readContactOptInResult(
    await requestJson("/api/v1/notification-contact-opt-ins/confirm", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  );
}

export async function updateNotificationContact(
  tenantId: string,
  contactId: string,
  input: UpdateNotificationContactInput,
): Promise<NotificationContact> {
  return readContact(
    await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-contacts/${encodeURIComponent(contactId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
    "contact",
  );
}

export async function deleteNotificationContact(tenantId: string, contactId: string): Promise<void> {
  await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-contacts/${encodeURIComponent(contactId)}`, { method: "DELETE" });
}

export async function listNotificationGroups(tenantId: string): Promise<readonly NotificationGroup[]> {
  const payload: unknown = await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-groups`);
  if (!isRecord(payload) || !Array.isArray(payload.groups)) {
    throw new Error("Notification group list response is invalid");
  }
  return payload.groups.map((group: unknown, index: number): NotificationGroup =>
    readGroup(group, `groups.${index}`),
  );
}

export async function createNotificationGroup(
  tenantId: string,
  input: SaveNotificationGroupInput,
): Promise<NotificationGroup> {
  return readGroup(
    await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-groups`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
    "group",
  );
}

export async function updateNotificationGroup(
  tenantId: string,
  groupId: string,
  input: SaveNotificationGroupInput,
): Promise<NotificationGroup> {
  return readGroup(
    await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-groups/${encodeURIComponent(groupId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
    "group",
  );
}

export async function deleteNotificationGroup(tenantId: string, groupId: string): Promise<void> {
  await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-groups/${encodeURIComponent(groupId)}`, { method: "DELETE" });
}

export async function listNotificationTemplates(tenantId: string): Promise<readonly NotificationTemplate[]> {
  const payload: unknown = await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-templates`);
  if (!isRecord(payload) || !Array.isArray(payload.templates)) {
    throw new Error("Notification template list response is invalid");
  }
  return payload.templates.map((template: unknown, index: number): NotificationTemplate =>
    readTemplate(template, `templates.${index}`),
  );
}

export async function createNotificationTemplate(
  tenantId: string,
  input: SaveNotificationTemplateInput,
): Promise<NotificationTemplate> {
  return readTemplate(
    await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-templates`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
    "template",
  );
}

export async function updateNotificationTemplate(
  tenantId: string,
  templateId: string,
  input: SaveNotificationTemplateInput,
): Promise<NotificationTemplate> {
  return readTemplate(
    await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-templates/${encodeURIComponent(templateId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
    "template",
  );
}

export async function deleteNotificationTemplate(tenantId: string, templateId: string): Promise<void> {
  await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-templates/${encodeURIComponent(templateId)}`, { method: "DELETE" });
}

export async function listNotificationPolicies(tenantId: string): Promise<readonly NotificationEventPolicy[]> {
  const payload: unknown = await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-policies`);
  if (!isRecord(payload) || !Array.isArray(payload.policies)) {
    throw new Error("Notification policy list response is invalid");
  }
  return payload.policies.map((policy: unknown, index: number): NotificationEventPolicy =>
    readPolicy(policy, `policies.${index}`),
  );
}

export async function updateNotificationPolicy(
  tenantId: string,
  eventFamily: NotificationEventFamily,
  input: UpdateNotificationEventPolicyInput,
): Promise<NotificationEventPolicy> {
  return readPolicy(
    await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/notification-policies/${encodeURIComponent(eventFamily)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
    "policy",
  );
}
